import { useCallback, useEffect, useState } from 'react';
import { Ban, Copy, Download, FileText, Loader2, Mail, Pencil, RefreshCw, Trash2, FilePenLine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { notasFiscaisApi, formatarDocumento, formatarMoeda, mensagemErro } from '@/lib/notasFiscaisApi';
import { STATUS_NOTA_CLASSE, STATUS_NOTA_LABEL, type NotaFiscal } from '@/types/notasFiscais';

type Acao = 'cancelar' | 'carta' | 'email' | null;

const INTERVALO_CONSULTA_MS = 4000;
const MAX_CONSULTAS = 20;
const PRAZO_CANCELAMENTO_MS = 24 * 60 * 60 * 1000;

interface NotaFiscalDetalheDialogProps {
  notaId: string | null;
  onOpenChange: (open: boolean) => void;
  onAtualizada: () => void;
  onEditar: (id: string) => void;
  onUsarComoBase: (id: string) => void;
}

function formatarData(data: string | null) {
  return data ? new Date(data).toLocaleString('pt-BR') : '-';
}

function formatarChave(chave: string | null) {
  return chave ? chave.replace(/(\d{4})(?=\d)/g, '$1 ') : '-';
}

export function NotaFiscalDetalheDialog({
  notaId,
  onOpenChange,
  onAtualizada,
  onEditar,
  onUsarComoBase,
}: NotaFiscalDetalheDialogProps) {
  const { toast } = useToast();
  const [nota, setNota] = useState<NotaFiscal | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [acao, setAcao] = useState<Acao>(null);
  const [texto, setTexto] = useState('');
  const [executando, setExecutando] = useState(false);

  const carregar = useCallback(async (id: string) => {
    setCarregando(true);
    try {
      setNota(await notasFiscaisApi.obterNota(id));
    } catch (e) {
      toast({ title: 'Erro ao carregar nota', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setCarregando(false);
    }
  }, [toast]);

  useEffect(() => {
    setNota(null);
    setAcao(null);
    if (notaId) carregar(notaId);
  }, [notaId, carregar]);

  const sincronizar = useCallback(async (silencioso = false) => {
    if (!notaId) return;
    setSincronizando(true);
    try {
      const atual = await notasFiscaisApi.sincronizarNota(notaId);
      setNota(atual);
      if (atual.status !== 'processando') onAtualizada();
    } catch (e) {
      if (!silencioso) toast({ title: 'Erro ao consultar a SEFAZ', description: mensagemErro(e), variant: 'destructive' });
      const salva = await notasFiscaisApi.obterNota(notaId).catch(() => null);
      if (salva) {
        setNota(salva);
        if (salva.status !== 'processando') onAtualizada();
      }
    } finally {
      setSincronizando(false);
    }
  }, [notaId, onAtualizada, toast]);

  useEffect(() => {
    if (nota?.status !== 'processando') return;
    let consultas = 0;
    const timer = setInterval(() => {
      consultas += 1;
      if (consultas > MAX_CONSULTAS) {
        clearInterval(timer);
        return;
      }
      sincronizar(true);
    }, INTERVALO_CONSULTA_MS);
    return () => clearInterval(timer);
  }, [nota?.status, sincronizar]);

  const abrirAcao = (nova: Acao) => {
    setTexto(nova === 'email' ? nota?.destinatario?.email || '' : '');
    setAcao(nova);
  };

  const executarAcao = async () => {
    if (!nota || !acao) return;
    setExecutando(true);
    try {
      switch (acao) {
        case 'cancelar':
          setNota(await notasFiscaisApi.cancelarNota(nota.id, texto));
          toast({ title: 'NF-e cancelada' });
          break;
        case 'carta':
          setNota(await notasFiscaisApi.cartaCorrecao(nota.id, texto));
          toast({ title: 'Carta de correção registrada' });
          break;
        case 'email': {
          const emails = texto.split(/[;,\s]+/).filter(Boolean);
          await notasFiscaisApi.enviarEmail(nota.id, emails);
          toast({ title: 'E-mail enviado', description: emails.join(', ') });
          break;
        }
        default: {
          const exaustivo: never = acao;
          throw new Error(`Ação desconhecida: ${String(exaustivo)}`);
        }
      }
      setAcao(null);
      onAtualizada();
    } catch (e) {
      toast({ title: 'Operação não concluída', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setExecutando(false);
    }
  };

  const excluir = async () => {
    if (!nota || !window.confirm('Excluir esta nota? Essa ação não pode ser desfeita.')) return;
    try {
      await notasFiscaisApi.excluirNota(nota.id);
      toast({ title: 'Nota excluída' });
      onAtualizada();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Erro ao excluir', description: mensagemErro(e), variant: 'destructive' });
    }
  };

  const editavel = nota?.status === 'rascunho' || nota?.status === 'erro';
  const dentroPrazoCancelamento =
    nota?.autorizada_em && Date.now() - new Date(nota.autorizada_em).getTime() < PRAZO_CANCELAMENTO_MS;
  const d = nota?.destinatario || {};

  const tituloAcao: Record<Exclude<Acao, null>, string> = {
    cancelar: 'Cancelar NF-e',
    carta: 'Carta de correção',
    email: 'Enviar NF-e por e-mail',
  };

  return (
    <Dialog open={Boolean(notaId)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            {nota?.numero ? `NF-e nº ${nota.numero} / série ${nota.serie}` : 'NF-e'}
            {nota && <Badge className={STATUS_NOTA_CLASSE[nota.status]}>{STATUS_NOTA_LABEL[nota.status]}</Badge>}
            {nota?.ambiente === 'homologacao' && <Badge variant="outline">Homologação</Badge>}
          </DialogTitle>
          <DialogDescription>
            {nota ? `${d.nome || 'Destinatário'} - ${formatarMoeda(nota.valor_total)}` : 'Carregando...'}
          </DialogDescription>
        </DialogHeader>

        {carregando || !nota ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <div className="space-y-5 text-sm">
            {nota.status === 'processando' && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 p-3 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Aguardando o retorno da SEFAZ...
              </div>
            )}
            {(nota.status === 'erro' || nota.status === 'denegada') && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-1">
                <p className="font-medium text-destructive">{nota.mensagem_sefaz || 'Nota rejeitada'}</p>
                {Array.isArray(nota.erros) && nota.erros.length > 0 && (
                  <ul className="list-disc pl-5">
                    {nota.erros.map((e, i) => <li key={i}>{e.mensagem}{e.campo ? ` (${e.campo})` : ''}</li>)}
                  </ul>
                )}
                {nota.status === 'erro' && <p>Corrija os dados em "Editar" e emita novamente.</p>}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <div><span className="text-muted-foreground">Chave de acesso:</span> <span className="font-mono text-xs">{formatarChave(nota.chave_nfe)}</span></div>
              <div><span className="text-muted-foreground">Protocolo:</span> {nota.protocolo || '-'}</div>
              <div><span className="text-muted-foreground">Emitida em:</span> {formatarData(nota.emitida_em)}</div>
              <div><span className="text-muted-foreground">Autorizada em:</span> {formatarData(nota.autorizada_em)}</div>
              {nota.cancelada_em && (
                <div className="md:col-span-2">
                  <span className="text-muted-foreground">Cancelada em:</span> {formatarData(nota.cancelada_em)} - {nota.justificativa_cancelamento}
                </div>
              )}
              <div className="md:col-span-2">
                <span className="text-muted-foreground">Destinatário:</span> {d.nome} - {formatarDocumento(d.cnpj || d.cpf)}
                {d.municipio ? ` - ${d.municipio}/${d.uf}` : ''}
              </div>
              {nota.conta_receber_id && (
                <div className="md:col-span-2 text-muted-foreground">
                  Vinculada a uma conta a receber{nota.danfe_url ? ' (DANFE anexada)' : ''}.
                </div>
              )}
            </div>

            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Descrição</TableHead>
                    <TableHead>NCM</TableHead>
                    <TableHead>CFOP</TableHead>
                    <TableHead>CSOSN</TableHead>
                    <TableHead className="text-right">Qtd.</TableHead>
                    <TableHead className="text-right">Valor un.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(nota.itens || []).map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.descricao}</TableCell>
                      <TableCell>{item.ncm}</TableCell>
                      <TableCell>{item.cfop || '-'}</TableCell>
                      <TableCell>{item.csosn || '-'}</TableCell>
                      <TableCell className="text-right">{item.quantidade} {item.unidade}</TableCell>
                      <TableCell className="text-right">{formatarMoeda(item.valor_unitario)}</TableCell>
                      <TableCell className="text-right">
                        {formatarMoeda(item.quantidade * item.valor_unitario - (item.valor_desconto || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap gap-2">
              {nota.danfe_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={nota.danfe_url} target="_blank" rel="noopener noreferrer"><FileText className="h-4 w-4 mr-1" /> DANFE (PDF)</a>
                </Button>
              )}
              {nota.xml_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={nota.xml_url} target="_blank" rel="noopener noreferrer"><Download className="h-4 w-4 mr-1" /> XML</a>
                </Button>
              )}
              {nota.xml_cancelamento_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={nota.xml_cancelamento_url} target="_blank" rel="noopener noreferrer"><Download className="h-4 w-4 mr-1" /> XML do cancelamento</a>
                </Button>
              )}
              {(nota.cartas_correcao || []).map((c) => (
                <Button key={c.numero} variant="outline" size="sm" asChild disabled={!c.pdf_url}>
                  <a href={c.pdf_url || '#'} target="_blank" rel="noopener noreferrer"><FilePenLine className="h-4 w-4 mr-1" /> CC-e nº {c.numero}</a>
                </Button>
              ))}
            </div>

            {acao && (
              <div className="rounded-md border p-4 space-y-2">
                <Label>{tituloAcao[acao]}</Label>
                {acao === 'email' ? (
                  <Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="email@cliente.com; outro@cliente.com" />
                ) : (
                  <>
                    <Textarea
                      rows={3}
                      value={texto}
                      onChange={(e) => setTexto(e.target.value)}
                      placeholder={
                        acao === 'cancelar'
                          ? 'Motivo do cancelamento (mínimo 15 caracteres)'
                          : 'Descreva a correção (mínimo 15 caracteres). Não é possível corrigir valores, impostos, destinatário ou data.'
                      }
                    />
                    <p className="text-xs text-muted-foreground">{texto.trim().length} caracteres</p>
                  </>
                )}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setAcao(null)} disabled={executando}>Voltar</Button>
                  <Button
                    size="sm"
                    variant={acao === 'cancelar' ? 'destructive' : 'default'}
                    onClick={executarAcao}
                    disabled={executando || (acao !== 'email' && texto.trim().length < 15)}
                  >
                    {executando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Confirmar
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {nota && (
          <DialogFooter className="gap-2 flex-wrap sm:justify-between">
            <div className="flex gap-2 flex-wrap">
              {editavel && (
                <>
                  <Button variant="outline" onClick={() => onEditar(nota.id)}><Pencil className="h-4 w-4 mr-1" /> Editar</Button>
                  <Button variant="ghost" onClick={excluir}><Trash2 className="h-4 w-4 mr-1" /> Excluir</Button>
                </>
              )}
              <Button variant="outline" onClick={() => onUsarComoBase(nota.id)}>
                <Copy className="h-4 w-4 mr-1" /> Usar como base
              </Button>
              {nota.status !== 'rascunho' && (
                <Button variant="outline" onClick={() => sincronizar()} disabled={sincronizando}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${sincronizando ? 'animate-spin' : ''}`} /> Consultar SEFAZ
                </Button>
              )}
            </div>
            {nota.status === 'autorizada' && (
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={() => abrirAcao('email')}><Mail className="h-4 w-4 mr-1" /> Enviar por e-mail</Button>
                <Button variant="outline" onClick={() => abrirAcao('carta')}><FilePenLine className="h-4 w-4 mr-1" /> Carta de correção</Button>
                <Button
                  variant="destructive"
                  onClick={() => abrirAcao('cancelar')}
                  disabled={!dentroPrazoCancelamento}
                  title={dentroPrazoCancelamento ? undefined : 'Prazo de 24 horas para cancelamento encerrado'}
                >
                  <Ban className="h-4 w-4 mr-1" /> Cancelar NF-e
                </Button>
              </div>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
